/**
 * v3.sessions — /v3/sessions/* (5 routes).
 */

import type {
  RequestOptions,
  ApiResult,
  SessionDto,
  CreateSessionPayload,
  SessionActionPayload,
  SessionListFilters,
  SessionCloseInput,
  GenericResult,
  Iso8601,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface SessionsListResponse {
  success: boolean;
  count: number;
  sessions: ReadonlyArray<SessionDto>;
}

export interface SessionSingleResponse {
  success: boolean;
  session?: SessionDto;
  error?: string;
}

export interface SessionHeartbeatResponse {
  success: boolean;
  sessionId?: string;
  lastHeartbeat?: Iso8601;
  error?: string;
}

export class SessionsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public create(
    req: CreateSessionPayload,
    opts?: RequestOptions
  ): Promise<SessionSingleResponse> {
    return this.http.post<SessionSingleResponse>('/v3/sessions', { ...opts, body: req });
  }

  public list(filters: SessionListFilters = {}, opts?: RequestOptions): Promise<SessionsListResponse> {
    return this.http.get<SessionsListResponse>('/v3/sessions', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(sessionId: string, opts?: RequestOptions): Promise<SessionSingleResponse> {
    return this.http.get<SessionSingleResponse>(
      `/v3/sessions/${encodeURIComponent(sessionId)}`,
      { ...opts }
    );
  }

  public updateHeartbeat(
    sessionId: string,
    opts?: RequestOptions
  ): Promise<SessionHeartbeatResponse> {
    return this.http.post<SessionHeartbeatResponse>(
      `/v3/sessions/${encodeURIComponent(sessionId)}/heartbeat`,
      { ...opts, body: { clientTimestamp: new Date().toISOString() } }
    );
  }

  public updateLastAction(
    sessionId: string,
    payload: SessionActionPayload,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/v3/sessions/${encodeURIComponent(sessionId)}/last-action`,
      { ...opts, body: payload }
    );
  }

  public close(
    sessionId: string,
    req: SessionCloseInput,
    opts?: RequestOptions
  ): Promise<ApiResult<SessionDto>> {
    return this.http.post<ApiResult<SessionDto>>(
      `/v3/sessions/${encodeURIComponent(sessionId)}/close`,
      { ...opts, body: req }
    );
  }
}

export type { ApiResult, SessionDto };
